// Per-kilo order builder: resolves the chosen Selection + Weight variant from server-rendered data,
// keeps input[name="id"] in sync, shows Order total (variant price × quantity) and $/kg (variant price ÷ kg),
// switches the requirements field between Mixed lot and Picked to your specs, and blocks add to cart
// in Picked mode until specs are entered.
class SWeightGrade extends HTMLElement {
  #ac;
  #rafId = 0;
  #variants = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    const dataEl = this.querySelector('script[data-wg-variants]');
    try {
      this.#variants = JSON.parse(dataEl?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name === 'wg-weight' || target.name === 'wg-selection') {
        this.#update(true);
      } else if (target.name === 'quantity') {
        this.#update(true);
      }
    }, { signal });

    this.addEventListener('input', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name === 'quantity') this.#update(false);
      if (target.matches('[data-wg-specs-input]') && target.value.trim() !== '') this.#showSpecsError(false);
    }, { signal });

    this.addEventListener('s-quantity:change', () => this.#update(true), { signal });

    // Capture phase runs before <s-product-form> and the native form submit, so every add-to-cart
    // path in this form (main button, mobile sticky bar, Enter key) respects the required specs rule.
    this.addEventListener('submit', (event) => {
      if (!this.#isPicked()) return;
      const input = this.#specsInput();
      if (!input || input.value.trim() !== '') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showSpecsError(true);
      input.focus();
    }, { signal, capture: true });

    this.#update(false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#rafId);
  }

  #value(name) {
    const input = this.querySelector(`input[name="${name}"]:checked`);
    return input instanceof HTMLInputElement ? input.value : null;
  }

  #quantity() {
    const input = this.querySelector('input[name="quantity"]');
    const parsed = input instanceof HTMLInputElement ? Number.parseInt(input.value, 10) : 1;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  #isPicked() {
    const selection = this.#value('wg-selection');
    return selection !== null && selection === this.dataset.pickedValue;
  }

  #specsInput() {
    const input = this.querySelector('[data-wg-specs-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #find(weight, selection) {
    return this.#variants.find((v) => v.weight === weight && (v.selection === null || v.selection === selection)) || null;
  }

  #formatMoney(cents) {
    const format = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) / 100;
    const group = (num, decimals, thousands, decimal) => {
      const fixed = num.toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0, ',', '.');
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return group(value, 0, ' ', ',');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return group(value, 2, ' ', '.');
        default: return group(value, 2, ',', '.');
      }
    });
  }

  #showSpecsError(show) {
    const error = this.querySelector('[data-wg-specs-error]');
    if (error instanceof HTMLElement) error.hidden = !show;
    const input = this.#specsInput();
    if (input) {
      if (show) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  }

  #updateSpecs(canBuy = true) {
    const box = this.querySelector('[data-wg-specs]');
    if (!(box instanceof HTMLElement)) return;
    const picked = this.#isPicked();
    box.hidden = !picked || !canBuy;
    const label = box.querySelector('[data-wg-specs-label]');
    const hint = box.querySelector('[data-wg-specs-hint]');
    if (label) label.textContent = picked ? box.dataset.labelPicked || '' : box.dataset.labelMixed || '';
    if (hint) hint.textContent = picked ? box.dataset.hintPicked || '' : box.dataset.hintMixed || '';
    const input = this.#specsInput();
    if (input) {
      // Mixed lot hides and disables the field (not submitted); the typed text is kept for switching back.
      input.disabled = !picked;
      if (picked) input.setAttribute('aria-required', 'true');
      else input.removeAttribute('aria-required');
    }
    if (!picked || (input && input.value.trim() !== '')) this.#showSpecsError(false);
  }

  #update(announce) {
    const weight = this.#value('wg-weight');
    const selection = this.#value('wg-selection');
    const variant = this.#find(weight, selection);
    const quantity = this.#quantity();

    const idInput = this.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement && variant) idInput.value = String(variant.id);

    const totalText = variant ? this.#formatMoney(Number(variant.price) * quantity) : '';
    const rateText = variant ? variant.perKg : '';

    this.querySelectorAll('[data-wg-total]').forEach((el) => { el.textContent = totalText; });
    this.querySelectorAll('[data-wg-rate]').forEach((el) => {
      el.textContent = rateText;
      el.hidden = rateText === '';
    });

    const canBuy = Boolean(variant && variant.available);
    this.querySelectorAll('[data-wg-buy]').forEach((el) => { el.hidden = !canBuy; });
    this.querySelectorAll('[data-wg-soldout]').forEach((el) => { el.hidden = canBuy; });
    this.querySelectorAll('[data-wg-quote]').forEach((el) => { el.hidden = true; });

    this.#updateSpecs(canBuy);

    // Keep the quote link naming the weight the buyer picked.
    if (weight) {
      this.querySelectorAll('a[data-wg-quote-link]').forEach((link) => {
        if (!(link instanceof HTMLAnchorElement)) return;
        const href = new URL(link.href, window.location.origin);
        href.searchParams.set('weight', weight);
        link.href = href.toString();
      });
    }

    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url);
    }

    if (announce) this.#announce(totalText);
  }

  #announce(total) {
    const region = this.querySelector('[data-wg-live]');
    if (!region || !total) return;
    const template = this.dataset.updatedMessage || '';
    cancelAnimationFrame(this.#rafId);
    region.textContent = '';
    this.#rafId = requestAnimationFrame(() => {
      region.textContent = template.replace('__TOTAL__', total);
    });
  }
}

if (!customElements.get('s-weight-grade')) customElements.define('s-weight-grade', SWeightGrade);
